import React, { useState, useEffect, useCallback } from 'react';
import '../styles/pages/_admin-suggestions.scss';
import { getAdminSuggestions, AdminSuggestionsResponse } from '../logic/suggestions/suggestionsApi';
import { formatDate } from '../utils/dateFormatter';

// Loading Spinner Component
const LoadingSpinner: React.FC = () => (
  <div className="admin-suggestions__spinner">
    <div className="admin-suggestions__spinner-circle"></div>
  </div>
);

const AdminSuggestionsPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [pagination, setPagination] = useState<AdminSuggestionsResponse['data']['pagination'] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState<{type: 'success' | 'error' | 'info', message: string} | null>(null);

  // Fetch suggestions from API
  const fetchSuggestions = useCallback(async (page: number = 1, search: string = '') => {
    try {
      setIsFetching(true);

      const token = localStorage.getItem('token');
      if (!token) {
        setNotification({
          type: 'error',
          message: 'Authentication required. Please log in again.'
        });
        return;
      }

      const response = await getAdminSuggestions(token, page, 10, search);

      setSuggestions(response.data.suggestions);
      setPagination(response.data.pagination);
      setNotification(null);

      // Sync currentPage with API response if it differs
      if (response.data.pagination?.currentPage && response.data.pagination.currentPage !== currentPage) {
        setCurrentPage(response.data.pagination.currentPage);
      }
    } catch (error: any) {

      setNotification({
        type: 'error',
        message: error.message || 'Failed to load suggestions. Please try again.'
      });
      // Reset to empty array on error
      setSuggestions([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  }, [currentPage]);

  const totalItems = pagination?.totalSuggestions || 0;
  const totalPages = pagination?.totalPages || 1;

  // Load initial data
  useEffect(() => {
    fetchSuggestions(1, '');
  }, [fetchSuggestions]);

  // Handle page changes
  useEffect(() => {
    // Only fetch if we have a valid page number and are not in initial loading state
    if (currentPage >= 1) {
      fetchSuggestions(currentPage, searchTerm);
    }
  }, [currentPage, searchTerm, fetchSuggestions]);

  // Handle search with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') {
        setCurrentPage(1); // Reset to first page when searching
        fetchSuggestions(1, searchTerm);
      } else {
        fetchSuggestions(1, '');
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, fetchSuggestions]);

  // Auto-dismiss notifications after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => {
    if (pagination?.hasPrev && currentPage > 1) {
      setCurrentPage(Math.max(1, currentPage - 1));
    }
  };

  const handleNextPage = () => {
    if (pagination?.hasNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  return (
    <div className="admin-suggestions">
      <main className="admin-suggestions__main">
        <div className="admin-suggestions__container">
          {/* Header Section */}
          <header className="admin-suggestions__header">
            <div className="admin-suggestions__header-content">
              <div className="admin-suggestions__title-section">
                <h1 className="admin-suggestions__title">User Suggestions</h1>
                <div className="admin-suggestions__count">
                  <span className="admin-suggestions__count-dot"></span>
                  {totalItems} Total Submissions
                </div>
              </div>

              {/* Search & Filter Controls */}
              <div className="admin-suggestions__controls">
                <div className="admin-suggestions__search-wrapper">
                  <span className="material-symbols-outlined admin-suggestions__search-icon">search</span>
                  <input
                    type="text"
                    className="admin-suggestions__search-input"
                    placeholder="Search suggestions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="admin-suggestions__search-clear"
                      onClick={clearSearch}
                      aria-label="Clear search"
                    >
                      <span className="material-symbols-outlined">close</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Notification */}
          {notification && (
            <div className={`admin-suggestions__notification admin-suggestions__notification--${notification.type}`}>
              <span className="material-symbols-outlined">
                {notification.type === 'success' ? 'check_circle' :
                 notification.type === 'error' ? 'error' : 'info'}
              </span>
              <span>{notification.message}</span>
            </div>
          )}

          {/* Suggestions List */}
          <div className="admin-suggestions__content">
            {isFetching && (
              <div className="admin-suggestions__loading">
                <LoadingSpinner />
                <span>Loading suggestions...</span>
              </div>
            )}

            {isLoading ? (
              // Loading skeleton
              <div className="admin-suggestions__skeleton">
                {Array.from({ length: 5 }, (_, index) => (
                  <div key={index} className="admin-suggestions__skeleton-card">
                    <div className="admin-suggestions__skeleton-header">
                      <div className="admin-suggestions__skeleton-title"></div>
                      <div className="admin-suggestions__skeleton-meta"></div>
                    </div>
                    <div className="admin-suggestions__skeleton-content">
                      <div className="admin-suggestions__skeleton-line"></div>
                      <div className="admin-suggestions__skeleton-line admin-suggestions__skeleton-line--short"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : suggestions.length === 0 ? (
              <div className="admin-suggestions__empty">
                <span className="material-symbols-outlined admin-suggestions__empty-icon">lightbulb</span>
                <h3 className="admin-suggestions__empty-title">No suggestions found</h3>
                <p className="admin-suggestions__empty-message">
                  {searchTerm ? 'Try adjusting your search terms.' : 'Users haven\'t submitted any suggestions yet.'}
                </p>
              </div>
            ) : (
              <div className="admin-suggestions__list">
                {suggestions.map((suggestion) => (
                  <div key={suggestion.id} className="admin-suggestions__card">
                    <div className="admin-suggestions__card-content">
                      <h3 className="admin-suggestions__card-title">{suggestion.title}</h3>
                      <p className="admin-suggestions__card-description">
                        {suggestion.description.length > 200
                          ? `${suggestion.description.substring(0, 200)}...`
                          : suggestion.description
                        }
                      </p>
                      <div className="admin-suggestions__card-footer">
                        <div className="admin-suggestions__card-meta">
                          <div className="admin-suggestions__avatar">
                            <span className="material-symbols-outlined">person</span>
                          </div>
                          <span>
                            Submitted by <strong>
                              {suggestion.createdBy.preferredName ||
                               suggestion.createdBy.firstName ||
                               suggestion.createdBy.email}
                            </strong> • {formatDate(suggestion.createdAt)}
                          </span>
                        </div>
                        {suggestion.description.length > 200 && (
                          <button className="admin-suggestions__view-more">
                            <span>View Details</span>
                            <span className="material-symbols-outlined">arrow_forward</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <nav aria-label="Suggestions pagination" className="admin-suggestions__pagination">
                <span className="admin-suggestions__pagination-info">
                  Showing <span className="admin-suggestions__pagination-highlight">
                    {Math.max(1, ((currentPage - 1) * 10) + 1)}-{Math.max(1, ((currentPage - 1) * 10) + (suggestions?.length || 0))}
                  </span> of <span className="admin-suggestions__pagination-highlight">{totalItems}</span>
                </span>
                <ul className="admin-suggestions__pagination-list">
                  <li>
                    <button
                      className="admin-suggestions__pagination-link admin-suggestions__pagination-link--previous"
                      onClick={handlePrevPage}
                      disabled={!pagination?.hasPrev}
                    >
                      Previous
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                    <li key={page}>
                      <button
                        className={`admin-suggestions__pagination-link ${
                          page === currentPage ? 'admin-suggestions__pagination-link--active' : ''
                        }`}
                        onClick={() => handlePageChange(page)}
                        disabled={isFetching}
                      >
                        {page}
                      </button>
                    </li>
                  ))}
                  <li>
                    <button
                      className="admin-suggestions__pagination-link admin-suggestions__pagination-link--next"
                      onClick={handleNextPage}
                      disabled={!pagination?.hasNext}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminSuggestionsPage;
